/**
 * ShaderProgram — компиляция, линковка, рефлексия униформ.
 *
 * - Шейдер-объекты detach + delete сразу после линковки (MDN best practice).
 * - Compile status НЕ проверяется — только link status (позволяет драйверу компилировать параллельно).
 * - Uniform locations кэшируются при первом обращении.
 * - Поддержка привязки UBO (Uniform Buffer Object) блоков.
 */
export class ShaderProgram
{
  public readonly program: WebGLProgram;
  private readonly locations = new Map<string, WebGLUniformLocation>();

  constructor( gl: WebGL2RenderingContext, vertex_source: string, fragment_source: string )
  {
    const vs = gl.createShader( gl.VERTEX_SHADER )!;
    const fs = gl.createShader( gl.FRAGMENT_SHADER )!;

    gl.shaderSource( vs, vertex_source );
    gl.shaderSource( fs, fragment_source );
    gl.compileShader( vs );
    gl.compileShader( fs );

    this.program = gl.createProgram()!;
    gl.attachShader( this.program, vs );
    gl.attachShader( this.program, fs );
    gl.linkProgram( this.program );

    // Ми свідомо прибрали перевірки gl.COMPILE_STATUS.
    // Виклик getShaderParameter(COMPILE_STATUS) змушує драйвер перервати паралельну
    // компіляцію і заблокувати основний потік (CPU stall).
    // Перевірка LINK_STATUS також блокує потік, якщо лінкування ще не завершилось,
    // але оскільки це єдина точка синхронізації, це дає драйверу шанс скомпілювати
    // VS і FS паралельно у фонових потоках (особливо з KHR_parallel_shader_compile).
    if ( !gl.getProgramParameter( this.program, gl.LINK_STATUS ) )
    {
      const info = gl.getProgramInfoLog( this.program );
      
      // Якщо помилка лінкування, виводимо логи шейдерів (вони компілювалися з помилками)
      const vs_info = gl.getShaderInfoLog( vs );
      const fs_info = gl.getShaderInfoLog( fs );
      if ( vs_info ) console.error( "Vertex Shader Error:\n", vs_info, "\nSource:\n", vertex_source );
      if ( fs_info ) console.error( "Fragment Shader Error:\n", fs_info, "\nSource:\n", fragment_source );

      gl.deleteProgram( this.program );
      throw new Error( `Shader link failed: ${info}` );
    }

    // Detach + delete шейдеры СРАЗУ после линковки (MDN: "delete objects eagerly")
    gl.detachShader( this.program, vs );
    gl.detachShader( this.program, fs );
    gl.deleteShader( vs );
    gl.deleteShader( fs );
  }

  /** Кэширует getUniformLocation при первом обращении */
  public getLocation( gl: WebGL2RenderingContext, name: string ): WebGLUniformLocation
  {
    let loc = this.locations.get( name );
    if ( loc === undefined )
    {
      loc = gl.getUniformLocation( this.program, name )!;
      this.locations.set( name, loc );
    }
    return loc;
  }

  /** Привязывает uniform block к глобальному binding point */
  public bindUBO( gl: WebGL2RenderingContext, block_name: string, binding_point: number ): void
  {
    const idx = gl.getUniformBlockIndex( this.program, block_name );
    if ( idx !== gl.INVALID_INDEX )
      gl.uniformBlockBinding( this.program, idx, binding_point );
  }

  public use( gl: WebGL2RenderingContext ): void
  {
    gl.useProgram( this.program );
  }

  public dispose( gl: WebGL2RenderingContext ): void
  {
    gl.deleteProgram( this.program );
  }
}
